import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Phone, Sparkles } from "lucide-react";
import { PHONE, PHONE_HREF } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUGGESTIONS = [
  "My drain is blocked — help!",
  "How much does a bathroom reno cost?",
  "Do you service Healesville?",
  "Hot water stopped working",
];

const genSessionId = () => {
  const existing = localStorage.getItem("styne_chat_session");
  if (existing) return existing;
  const fresh = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  localStorage.setItem("styne_chat_session", fresh);
  return fresh;
};

// Renders Claude's response with **bold** markdown support and preserved line breaks
const renderRich = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={i} className="text-gold">{p.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

const AskStyneChatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(genSessionId);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const send = useCallback(async (text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || streaming) return;
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", text: trimmed },
      { role: "assistant", text: "" },
    ]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: trimmed }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const chunk of chunks) {
          if (!chunk.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(chunk.slice(6));
            if (payload.type === "delta") {
              setMessages((m) => {
                const next = [...m];
                next[next.length - 1] = {
                  role: "assistant",
                  text: next[next.length - 1].text + payload.content,
                };
                return next;
              });
            }
          } catch (_) { /* ignore parse errors */ }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          text: "Sorry — I'm having trouble connecting. Please call Dylan on 0457 006 466 (24/7).",
        };
        return next;
      });
    } finally {
      setStreaming(false);
    }
  }, [sessionId, streaming]);

  const isEmergencyMsg = (t) => t && t.includes("🚨");

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-open-btn"
          className="fixed bottom-6 right-6 z-40 group flex items-center gap-3 pl-3 pr-5 py-3 bg-gold text-bg font-display tracking-widerx text-sm uppercase shadow-2xl hover:bg-goldHover transition-all hover:-translate-y-1"
          aria-label="Open Ask Styne chat"
        >
          <span className="relative flex items-center justify-center w-9 h-9 bg-bg/90 text-gold">
            <MessageCircle size={18} />
            <span className="absolute top-0 right-0 w-2.5 h-2.5 bg-gold border-2 border-bg rounded-full animate-pulse" />
          </span>
          <span className="hidden sm:inline-flex items-center gap-1">
            Ask Styne <Sparkles size={12} className="opacity-70" />
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:right-6 sm:bottom-6 z-50 w-auto sm:w-[400px] max-h-[85vh] bg-card border border-lineStrong shadow-2xl flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-300"
        >
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-line bg-elevated">
            <div className="relative w-10 h-10 bg-gold/10 border border-gold/30 flex items-center justify-center flex-shrink-0">
              <img src="/logo-icon.png" alt="Styne" className="w-8 h-8 object-contain" />
            </div>
            <div className="flex-1">
              <div className="font-display text-base tracking-widerx text-ink flex items-center gap-2">
                Ask Styne <span className="text-[9px] px-1.5 py-0.5 bg-gold/15 text-gold tracking-widerx">AI</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                Online · 24/7
              </div>
            </div>
            <a
              href={PHONE_HREF}
              className="hidden sm:flex items-center gap-1.5 text-gold hover:text-goldHover text-xs font-display tracking-widerx"
              data-testid="chatbot-call-shortcut"
            >
              <Phone size={12} /> Call
            </a>
            <button
              onClick={() => setOpen(false)}
              data-testid="chatbot-close-btn"
              className="text-muted hover:text-ink p-1"
              aria-label="Close chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px]"
            data-testid="chatbot-messages"
          >
            {messages.length === 0 && (
              <div className="text-center py-4 space-y-4">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-gold/10 border border-gold/30 mb-1">
                  <img src="/logo-icon.png" alt="" className="w-10 h-10 object-contain" />
                </div>
                <div>
                  <div className="font-display text-xl text-ink mb-2">G'day 👋</div>
                  <p className="text-muted text-sm max-w-xs mx-auto leading-relaxed">
                    I'm Ask Styne — I can answer questions about our services, help you book a quote, or point you to Dylan if it's an emergency.
                  </p>
                </div>
                <div className="pt-2 space-y-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`chatbot-suggestion-${i}`}
                      className="block w-full text-left text-sm text-ink border border-line hover:border-gold/40 hover:text-gold p-2.5 transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chatbot-msg-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-gold text-bg font-medium"
                      : isEmergencyMsg(m.text)
                      ? "bg-emergency/10 border border-emergency/40 text-ink"
                      : "bg-elevated border border-line text-ink"
                  }`}
                >
                  {m.text ? renderRich(m.text) : (
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 bg-muted rounded-full animate-bounce" style={{animationDelay:'0ms'}} />
                      <span className="w-1.5 h-1.5 bg-muted rounded-full animate-bounce" style={{animationDelay:'150ms'}} />
                      <span className="w-1.5 h-1.5 bg-muted rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
                    </span>
                  )}
                  {m.role === "assistant" && isEmergencyMsg(m.text) && (
                    <a
                      href={PHONE_HREF}
                      data-testid="chatbot-emergency-call"
                      className="mt-3 inline-flex items-center gap-2 bg-emergency text-white font-display text-xs tracking-widerx uppercase px-3 py-2 hover:bg-red-600 transition-colors"
                    >
                      <Phone size={12} /> Call {PHONE} Now
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="p-3 border-t border-line bg-elevated flex items-center gap-2"
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about plumbing, drainage, quotes…"
              disabled={streaming}
              data-testid="chatbot-input"
              className="flex-1 bg-transparent text-ink placeholder:text-muted text-sm px-3 py-2 outline-none border border-line focus:border-gold transition-colors disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="chatbot-send"
              className="w-10 h-10 flex items-center justify-center bg-gold text-bg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-goldHover transition-colors"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </form>
          <div className="px-4 pb-3 text-[10px] text-dim text-center tracking-widerx uppercase">
            Powered by Claude · For emergencies, call {PHONE}
          </div>
        </div>
      )}
    </>
  );
};

export default AskStyneChatbot;
